import type {
  MixpanelConfig,
  TelemetryConfig,
  DatadogConfig,
} from './types'

// Environment configuration
const isDevelopment = import.meta.env.MODE === 'development'
const isProduction = import.meta.env.MODE === 'production'

// Mixpanel configuration
export const mixpanelConfig: MixpanelConfig = {
  enabled: import.meta.env.VITE_MIXPANEL_ENABLED === 'true',
  environment: import.meta.env.MODE as 'development' | 'staging' | 'production',
  token: import.meta.env.VITE_MIXPANEL_TOKEN || '',
  debug: isDevelopment,
  track_pageview: false,
}

// Datadog configuration
export const datadogConfig: DatadogConfig = {
  enabled: import.meta.env.VITE_DATADOG_ENABLED === 'true',
  environment: (import.meta.env.VITE_DATADOG_ENV || import.meta.env.MODE) as 'development' | 'staging' | 'production',
  applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID || '',
  clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN || '',
  site: 'us5.datadoghq.com',
  service: 'cheiron-auth-frontend',
  version: import.meta.env.VITE_DATADOG_VERSION || '1.0.0',
  sessionSampleRate: Number(import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE) || 100,
  sessionReplaySampleRate: Number(import.meta.env.VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE) || 20,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  forwardErrorsToLogs: true,
  defaultPrivacyLevel: (import.meta.env.VITE_DATADOG_DEFAULT_PRIVACY_LEVEL as 'allow' | 'mask' | 'mask-user-input') || 'mask-user-input',
  debug: isDevelopment,
}

// Get complete telemetry configuration
export const getTelemetryConfig = (
  customConfig?: Partial<TelemetryConfig>
): TelemetryConfig => {
  const defaultConfig: TelemetryConfig = {
    mixpanel: mixpanelConfig,
    datadog: datadogConfig,
    // TODO: Add other service configs as they're implemented
    // webVitals: webVitalsConfig,
  }

  // Merge with custom config if provided
  if (customConfig) {
    return {
      ...defaultConfig,
      ...customConfig,
      // Deep merge service configs
      mixpanel: customConfig.mixpanel
        ? { ...defaultConfig.mixpanel, ...customConfig.mixpanel }
        : defaultConfig.mixpanel,
      datadog: customConfig.datadog
        ? { ...defaultConfig.datadog, ...customConfig.datadog }
        : defaultConfig.datadog,
    }
  }

  return defaultConfig
}

// Validate required configuration
export const validateTelemetryConfig = (): boolean => {
  if (mixpanelConfig.enabled && !mixpanelConfig.token) {
    console.warn(
      'Mixpanel is enabled but no token provided. Set VITE_MIXPANEL_TOKEN environment variable.'
    )
    return false
  }

  if (datadogConfig.enabled && (!datadogConfig.applicationId || !datadogConfig.clientToken)) {
    console.warn(
      'Datadog is enabled but applicationId or clientToken is missing. Set VITE_DATADOG_APPLICATION_ID and VITE_DATADOG_CLIENT_TOKEN environment variables.'
    )
    return false
  }

  return true
}
